import "globals.css";
import "bluetooth-landing.css";
import dayjs from "dayjs";
import "dayjs/locale/pl";
import localizedFormat from "dayjs/plugin/localizedFormat";
import type { AppProps } from "next/app";
import { useRouter } from "next/router";
import Script from "next/script";
import { appWithTranslation, useTranslation } from "next-i18next";
import { FunctionComponent, useEffect } from "react";
import { ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import { AppContextProvider } from "@context/context";
import { useCookiesConsent } from "@hooks";
import * as gtag from "@lib/gtag";
import { consent } from "@lib/gtag";

dayjs.extend(localizedFormat);

const env = process.env.NODE_ENV;

const MyApp: FunctionComponent<AppProps> = ({ Component, pageProps }) => {
  useTranslation();
  const router = useRouter();
  dayjs.locale(router.locale);

  const { cookiesConsent } = useCookiesConsent();
  const enableAnalytics = env === "production";

  useEffect(() => {
    if (enableAnalytics) {
      const handleRouteChange = (url: any) => gtag.pageview(url);
      router.events.on("routeChangeComplete", handleRouteChange);
      router.events.on("hashChangeComplete", handleRouteChange);
      return () => {
        router.events.off("routeChangeComplete", handleRouteChange);
        router.events.off("hashChangeComplete", handleRouteChange);
      };
    }
  }, [router.events, enableAnalytics]);

  useEffect(() => {
    if (enableAnalytics) {
      if (cookiesConsent) {
        console.log("consent granted");
        consent({
          ad_storage: "granted",
          ad_user_data: "granted",
          ad_personalization: "granted",
          analytics_storage: "granted",
        });
        (window as any).clarity("consent");
      } else {
        console.log("consent denied");
        consent({
          ad_storage: "denied",
          ad_user_data: "denied",
          ad_personalization: "denied",
          analytics_storage: "denied",
        });
        (window as any).clarity("consent", false);
      }
    }
  }, [cookiesConsent, enableAnalytics]);

  return (
    <>
      {/* Global Site Tag (gtag.js) - Google Analytics */}
      {enableAnalytics && (
        <>
          <Script
            id="clarity_script_tag"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
              (function(c,l,a,r,i,t,y){
                  c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                  t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                  y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
              })(window, document, "clarity", "script", "p9x09x0tl2");
          `,
            }}
          />

          <Script
            async
            src="https://scripts.simpleanalyticscdn.com/latest.js"
          />

          <Script
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${gtag.GA_TRACKING_ID}`}
          />

          <Script
            id="gtag-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('consent', 'default', {
              'ad_storage': 'denied',
              'ad_user_data': 'denied',
              'ad_personalization': 'denied',
              'analytics_storage': 'denied'
            });
            gtag('js', new Date());
            gtag('config', '${gtag.GA_TRACKING_ID}', {
              page_path: window.location.pathname,
              'cookie_flags': 'SameSite=None;Secure'
            });
            gtag('config', '${gtag.AW_TRACKING_ID}', {
              'cookie_flags': 'SameSite=None;Secure'
            });
          `,
            }}
          />
        </>
      )}
      <AppContextProvider>
        <Component {...pageProps} />
        <ToastContainer position="bottom-right" theme="dark" />
      </AppContextProvider>
    </>
  );
};

export default appWithTranslation(MyApp);
